import { useState, useEffect, useRef } from "react";
import {
  Box, Card, CardContent, Typography, Avatar, Button,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, CircularProgress, Snackbar, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, Radio
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

const token = () => localStorage.getItem("biyz_token");
const authHead = () => ({ Authorization: `Bearer ${token()}` });

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [form, setForm] = useState({
    name: "", surname: "", phone: "", age: "", gender: "", city: "",
    newsletter: false, avatar: "",
  });
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const fileRef = useRef();

  // ── Aboneligim / iptal akisi ──
  const [subInfo, setSubInfo] = useState(null);
  const [cxStep, setCxStep] = useState(0);   // 0 kapali, 1 emin misin, 2 neden, 3 vazgecirme, 4 sonuc
  const [cxReason, setCxReason] = useState("");
  const [cxNote, setCxNote] = useState("");
  const [cxBusy, setCxBusy] = useState(false);
  const [cxUntil, setCxUntil] = useState(null);
  useEffect(() => {
    fetch("/api/platform/subscription/me", { headers: authHead() })
      .then(r => r.json()).then(d => { if (d && d.ok) setSubInfo(d); }).catch(() => {});
  }, []);
  const doCancel = async () => {
    setCxBusy(true);
    try {
      const r = await fetch("/api/platform/subscription/cancel", {
        method: "POST", headers: { ...authHead(), "Content-Type": "application/json" },
        body: JSON.stringify({ reason: cxReason, note: cxNote }),
      });
      const d = await r.json();
      if (subInfo && subInfo.recurring) {
        try { await fetch("/api/payments/esnekpos/recurring/cancel", { method: "POST", headers: { ...authHead(), "Content-Type": "application/json" }, body: "{}" }); } catch {}
      }
      setCxUntil(d && d.accessUntil ? d.accessUntil : null);
      setCxStep(4);
      setSubInfo(s => s ? { ...s, cancelRequest: { date: new Date(), reason: cxReason } } : s);
    } catch { setSnack({ open: true, msg: "İptal işlemi başarısız — lütfen tekrar deneyin.", sev: "error" }); }
    finally { setCxBusy(false); }
  };
  const trDate = (d) => { try { return new Date(d).toLocaleDateString("tr-TR"); } catch { return ""; } };

  useEffect(() => {
    fetch("/api/platform/users/me", { headers: authHead() })
      .then(r => r.json())
      .then(u => {
        setForm({
          name: u.name || "",
          surname: u.surname || "",
          phone: u.phone || "",
          age: u.age || "",
          gender: u.gender || "",
          city: u.city || "",
          newsletter: !!u.newsletterConsent,
          avatar: u.avatar || "",
        });
        setAvatarPreview(u.avatar || "");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("surname", form.surname);
      fd.append("phone", form.phone);
      fd.append("age", form.age);
      fd.append("gender", form.gender);
      fd.append("city", form.city);
      if (avatarFile) fd.append("avatar", avatarFile);

      const res = await fetch("/api/platform/users/me", {
        method: "PUT",
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) throw new Error("Kayit basarisiz");

      await fetch("/api/platform/users/newsletter-consent", {
        method: "POST",
        headers: { ...authHead(), "Content-Type": "application/json" },
        body: JSON.stringify({ consent: form.newsletter }),
      });

      const updated = await res.json();
      const stored = JSON.parse(localStorage.getItem("biyz_user") || "{}");
      localStorage.setItem("biyz_user", JSON.stringify({
        ...stored,
        fullName: (form.name + " " + form.surname).trim(),
        avatar: updated.avatar || stored.avatar,
      }));

      setSnack({ open: true, msg: "Bilgileriniz kaydedildi.", sev: "success" });
    } catch {
      setSnack({ open: true, msg: "Kayit sirasinda hata olustu.", sev: "error" });
    } finally {
      setSaving(false);
    }
  };

  const user = (() => { try { return JSON.parse(localStorage.getItem("biyz_user") || "{}"); } catch { return {}; } })();

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 680, mx: "auto", px: 2, py: 4 }}>

      <Card sx={{ mb: 2, borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2.5, py: 3 }}>
          <Box sx={{ position: "relative" }}>
            <Avatar src={avatarPreview} sx={{ width: 72, height: 72, fontSize: 28, bgcolor: "#1565c0" }}>
              {!avatarPreview && (form.name || user.fullName || "U")[0]?.toUpperCase()}
            </Avatar>
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {(form.name + " " + form.surname).trim() || user.fullName || ""}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {user.email || ""}
            </Typography>
            <Button
              size="small"
              startIcon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
              onClick={() => fileRef.current && fileRef.current.click()}
              sx={{ color: "#1565c0", textTransform: "none", p: 0, fontSize: 13, fontWeight: 600 }}
            >
              Avatar Degistir
            </Button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleAvatarChange} />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
        <CardContent sx={{ pt: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Kisisel Bilgiler</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="Isim" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} size="small" />
            <TextField label="Soyisim" value={form.surname} onChange={e => setForm(f => ({ ...f, surname: e.target.value }))} size="small" />
            <TextField label="Telefon" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} size="small" />
            <TextField label="Yas" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} size="small" type="number" />
            <FormControl size="small">
              <InputLabel>Cinsiyet</InputLabel>
              <Select label="Cinsiyet" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                <MenuItem value="Erkek">Erkek</MenuItem>
                <MenuItem value="Kadin">Kadin</MenuItem>
                <MenuItem value="Diger">Diger</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Sehir" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} size="small" />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Tercihler</Typography>
          <FormControlLabel
            control={
              <Switch checked={form.newsletter}
                onChange={e => setForm(f => ({ ...f, newsletter: e.target.checked }))}
                color="primary" />
            }
            label={<Typography variant="body2">Bulten ve kampanya e-postalari almak istiyorum</Typography>}
          />
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ background: "linear-gradient(135deg,#1a6fc4,#2196f3)", borderRadius: 2, px: 4, fontWeight: 700 }}>
          {saving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "KAYDET"}
        </Button>
      </Box>

      {/* ── Aboneligim ── */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Aboneliğim</Typography>
          {!subInfo || subInfo.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aktif aboneliğiniz bulunmuyor.</Typography>
          ) : (<>
            {subInfo.items.map((it, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                <Typography variant="body2" fontWeight={600}>{it.packageName}</Typography>
                <Typography variant="caption" color="text.secondary">Bitiş: {trDate(it.endDate)}</Typography>
              </Box>
            ))}
            {subInfo.recurring && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Otomatik yenileme: AÇIK{subInfo.recurring.nextChargeDate ? ` (sonraki ödeme: ${trDate(subInfo.recurring.nextChargeDate)})` : ""}
              </Typography>
            )}
            {subInfo.cancelRequest ? (
              <Alert severity="info" sx={{ mt: 1 }}>İptal talebiniz alındı ({trDate(subInfo.cancelRequest.date)}). Erişiminiz dönem sonuna kadar devam eder.</Alert>
            ) : (
              <Button size="small" color="error" variant="outlined" sx={{ mt: 1, borderRadius: 2 }} onClick={() => { setCxStep(1); setCxReason(""); setCxNote(""); }}>
                Aboneliği İptal Et
              </Button>
            )}
          </>)}
        </CardContent>
      </Card>

      {/* ── İptal akışı ── */}
      <Dialog open={cxStep > 0} onClose={() => cxStep !== 4 && setCxStep(0)} maxWidth="xs" fullWidth>
        {cxStep === 1 && (<>
          <DialogTitle>Emin misiniz?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>Üyeliğiniz sonlandırılacaktır. İptal ederseniz:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>• Eğitmen botlarınıza erişim dönem sonunda kapanır</Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>• 🔥 Seriniz ve ⚡ XP ilerlemeniz durur</Typography>
            <Typography variant="body2">• Kişisel ders planınız ve seviye takibiniz askıya alınır</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setCxStep(0)}>Vazgeç</Button>
            <Button color="error" onClick={() => setCxStep(2)}>Devam Et</Button>
          </DialogActions>
        </>)}
        {cxStep === 2 && (<>
          <DialogTitle>Neden ayrılmak istiyorsunuz?</DialogTitle>
          <DialogContent>
            <RadioGroup value={cxReason} onChange={e => setCxReason(e.target.value)}>
              {["Fiyat yüksek geldi", "Yeterince kullanmıyorum", "Beklediğim faydayı görmedim", "Teknik sorunlar yaşadım", "Diğer"].map(r => (
                <FormControlLabel key={r} value={r} control={<Radio size="small" />} label={<Typography variant="body2">{r}</Typography>} />
              ))}
            </RadioGroup>
            <TextField fullWidth size="small" multiline minRows={2} placeholder="Eklemek istediğiniz bir şey var mı? (isteğe bağlı)"
              value={cxNote} onChange={e => setCxNote(e.target.value)} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCxStep(1)}>Geri</Button>
            <Button color="error" disabled={!cxReason} onClick={() => setCxStep(3)}>Devam Et</Button>
          </DialogActions>
        </>)}
        {cxStep === 3 && (<>
          <DialogTitle>Gitmeden önce… 🎁</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>Size özel: <b>%30 indirimle</b> devam edebilirsiniz. Paket yenilemede <b>biuzman</b> kodunu kullanın — 1400₺ yerine <b>980₺</b>.</Typography>
            <Typography variant="body2" color="text.secondary">Mevcut erişiminiz zaten dönem sonuna kadar sizde kalacak; acele karar vermenize gerek yok.</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => { setCxStep(0); window.location.href = "/packages"; }}>İndirimle Devam Et</Button>
            <Button color="error" disabled={cxBusy} onClick={doCancel}>{cxBusy ? "İptal ediliyor…" : "Yine de İptal Et"}</Button>
          </DialogActions>
        </>)}
        {cxStep === 4 && (<>
          <DialogTitle>Aboneliğiniz iptal edildi</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>Talebiniz işleme alındı{subInfo && subInfo.recurring ? " ve otomatik yenileme durduruldu" : ""}.</Typography>
            {cxUntil && <Typography variant="body2">Erişiminiz <b>{trDate(cxUntil)}</b> tarihine kadar devam edecek.</Typography>}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Fikrinizi değiştirirseniz kaldığınız yerden devam edebilirsiniz. 💜</Typography>
          </DialogContent>
          <DialogActions><Button variant="contained" onClick={() => setCxStep(0)}>Kapat</Button></DialogActions>
        </>)}
      </Dialog>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
        {["KVKK", "Gizlilik Politikasi", "Mesafeli Satis Sozlesmesi"].map(t => (
          <Typography key={t} variant="caption" color="text.secondary"
            sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
            {t}
          </Typography>
        ))}
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} sx={{ width: "100%" }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

